import useFieldId from 'core/hooks/useFieldId';
import moment from 'moment';
import { useEffect, useState } from 'react';

export default function useInputTimeRange({ endTimeLabel, error, label, minTime, name, onChange, startTimeLabel, t, value }) {
  const { baseId, labelId, validationId } = useFieldId('input-time-range');
  const [startTime, setStartTime] = useState(value[0]);
  const [endTime, setEndTime] = useState(value[1]);
  const [isError, setIsError] = useState(false);
  const [isErrorMin, setIsErrorMin] = useState(false);

  useEffect(() => {
    setStartTime(value[0]);
    setEndTime(value[1]);
    // Un valor nuevo del consumidor sustituye al que falló la validación (p. ej. filas que suben tras borrar una).
    setIsError(false);
    setIsErrorMin(false);
  }, [value]);

  const handleChangeStart = (e) => {
    setStartTime(e.target.value);
    if (!moment(e.target.value, 'HH:mm').isBefore(moment(endTime, 'HH:mm'))) {
      const newEndtime = moment(e.target.value, 'HH:mm').add(1, 'hours').format('HH:mm');
      setEndTime(newEndtime);
      handleChange(e.target.value, newEndtime);
    } else {
      handleChange(e.target.value, endTime);
    }
  };

  const handleChangeEnd = (e) => {
    setEndTime(e.target.value);
    handleChange(startTime, e.target.value);
  };

  const handleChange = (start, end) => {
    if (validate(start, end)) {
      onChange({
        target: { name: name, value: [start, end] },
      });
    } else {
      onChange({
        target: { name: name, value: [], validity: { valid: false, badInput: true } },
      });
    }
  };

  const validate = (start, end) => {
    let startTime = moment(start, 'hh:mm');
    let endTime = moment(end, 'hh:mm');
    let validate = startTime.isBefore(endTime) && start.length === 5 && end.length === 5;

    if (validate && validateMin(start)) {
      setIsError(false);
      return true;
    }

    setIsError(true);
    return false;
  };

  const validateMin = (start) => {
    if (minTime) {
      const startTime = moment(start, 'hh:mm');
      const minStartTime = moment(minTime, 'hh:mm');
      const isValid = startTime.isSameOrAfter(minStartTime);
      setIsErrorMin(!isValid);
      return isValid;
    }

    return true;
  };

  function getValidationMessage() {
    if (isErrorMin) return t('new-course:Start time must be later than the current time');
    // `error` cubre a los consumidores que guardan el estado inválido en su propio estado y vuelven a montar el átomo.
    if (isError || error) return t('new-course:You must enter a valid time');
    return null;
  }

  const validationMessage = getValidationMessage();
  const hasLabel = Boolean(label);
  // Los consumidores pasan un `&nbsp;` como etiqueta para alinear; solo una etiqueta de texto puede nombrar el grupo.
  const hasGroupName = typeof label === 'string' && label.trim() !== '';

  return {
    endInputId: `${baseId}-end`,
    endTime,
    endTimeName: endTimeLabel ?? t('new-course:End time'),
    groupLabelId: hasGroupName ? labelId : undefined,
    groupRole: hasGroupName ? 'group' : undefined,
    handleChangeEnd,
    handleChangeStart,
    hasLabel,
    inputDescribedBy: validationMessage ? validationId : undefined,
    isInvalid: isError || isErrorMin || error ? true : undefined,
    labelId,
    startInputId: `${baseId}-start`,
    startTime,
    startTimeName: startTimeLabel ?? t('new-course:Start time'),
    validationId,
    validationMessage,
  };
}
